// src/components/admin/products/tabs/LandingProducts.tsx
// Layout: editor em cima (full width, 3 colunas) + preview embaixo (full width, sem scroll, grande)
// ✅ ATUALIZADO: preço livre (priceLine1 + priceLine2) + bullets para todos os slots

import React, { useState, useEffect } from 'react';
import { Save, Eye, EyeOff, ChevronDown, Star, ArrowRight, Check, RefreshCw, Globe, Layers } from 'lucide-react';
import { useAdminProducts } from '../../../../hooks/useAdminQueries';
import { Product } from '../../../../types/marketplace.types';
import './LandingProducts.css';

interface LandingProductsProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

interface SlotConfig {
    productId: string;
    planIndex: number;
    customTitle: string;
    customDescription: string;
    customBullets: string[];
    showBadge: boolean;
    badgeText: string;
    ctaText: string;
    footerNote: string;
    // ✅ NOVO: preço totalmente livre
    priceLine1: string;  // ex: "R$ 197 à vista"
    priceLine2: string;  // ex: "ou 4x de R$ 54,85"
}

const EMPTY_SLOT: SlotConfig = {
    productId: '',
    planIndex: 0,
    customTitle: '',
    customDescription: '',
    customBullets: [
        'Todos os flashcards e atualizações futuras',
        'Todos os modos de estudo disponíveis',
        'Banco de questões completo',
        'Apostilas interativas integradas',
        'Métricas avançadas de desempenho',
        'Acesso aos grupos de estudo',
        'Suporte contínuo do Assistente Graia',
    ],
    showBadge: true,
    badgeText: 'Recomendado para constância',
    ctaText: 'Começar com Acesso Total',
    footerNote: '',
    priceLine1: '',
    priceLine2: '',
};

const EMPTY_SMALL_SLOT: SlotConfig = {
    productId: '',
    planIndex: 0,
    customTitle: '',
    customDescription: '',
    customBullets: [],
    showBadge: false,
    badgeText: '',
    ctaText: 'Ver detalhes',
    footerNote: '',
    priceLine1: '',
    priceLine2: '',
};

const DEFAULT_CONFIG = {
    sectionTitle: 'Escolha como quer avançar na graduação.',
    sectionSubtitle: 'Acesso total à plataforma ou foco cirúrgico em uma etapa específica.',
    slotMain: { ...EMPTY_SLOT },
    slotTopRight: { ...EMPTY_SMALL_SLOT },
    slotBottomRight: { ...EMPTY_SMALL_SLOT },
};

type ConfigState = typeof DEFAULT_CONFIG;

const LandingProducts: React.FC<LandingProductsProps> = ({ onShowToast }) => {
    const { data: productsData } = useAdminProducts();
    const products: Product[] = productsData?.products?.filter((p: Product) => p.active) ?? [];

    const [config, setConfig] = useState<ConfigState>(DEFAULT_CONFIG);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(true);
    const [activeSlot, setActiveSlot] = useState<'main' | 'topRight' | 'bottomRight'>('main');

    useEffect(() => {
        const load = async () => {
            try {
                const token = localStorage.getItem('accessToken');
                const res = await fetch('/api/admin/landing-config', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data.config) setConfig({ ...DEFAULT_CONFIG, ...data.config });
                }
            } catch { /* usa DEFAULT_CONFIG */ }
            finally { setLoading(false); }
        };
        load();
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('accessToken');
            const res = await fetch('/api/admin/landing-config', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ config })
            });
            if (res.ok) onShowToast('Landing page atualizada com sucesso!', 'success');
            else onShowToast('Erro ao salvar configuração', 'error');
        } catch { onShowToast('Erro ao salvar', 'error'); }
        finally { setSaving(false); }
    };

    const getTitle = (slot: SlotConfig) =>
        slot.customTitle || products.find(p => p._id === slot.productId)?.title || 'Selecione um produto';

    const getDescription = (slot: SlotConfig) =>
        slot.customDescription || products.find(p => p._id === slot.productId)?.description || '';

    const updateSlot = (slotKey: 'slotMain' | 'slotTopRight' | 'slotBottomRight', field: keyof SlotConfig, value: any) => {
        setConfig(prev => ({ ...prev, [slotKey]: { ...prev[slotKey], [field]: value } }));
    };

    // ── Bullets helpers — agora para qualquer slot ──
    const updateBullet = (slotKey: 'slotMain' | 'slotTopRight' | 'slotBottomRight', index: number, value: string) => {
        const bullets = [...config[slotKey].customBullets];
        bullets[index] = value;
        updateSlot(slotKey, 'customBullets', bullets);
    };

    const addBullet = (slotKey: 'slotMain' | 'slotTopRight' | 'slotBottomRight') =>
        updateSlot(slotKey, 'customBullets', [...config[slotKey].customBullets, 'Nova funcionalidade']);

    const removeBullet = (slotKey: 'slotMain' | 'slotTopRight' | 'slotBottomRight', i: number) =>
        updateSlot(slotKey, 'customBullets', config[slotKey].customBullets.filter((_, idx) => idx !== i));

    const slotKeyMap = {
        main: 'slotMain' as const,
        topRight: 'slotTopRight' as const,
        bottomRight: 'slotBottomRight' as const,
    };

    if (loading) return (
        <div className="lp-loading"><div className="lp-spinner" /><p>Carregando configuração...</p></div>
    );

    const activeSlotKey = slotKeyMap[activeSlot];
    const activeSlotData = config[activeSlotKey];
    const slotLabel = activeSlot === 'main' ? 'Card Principal (destaque)' : activeSlot === 'topRight' ? 'Card Topo Direita' : 'Card Base Direita';

    return (
        <div className="lp-root">

            {/* ── TOOLBAR ── */}
            <div className="lp-toolbar">
                <div className="lp-toolbar-left">
                    <h3>Editor — Landing Page · Bloco de Planos</h3>
                    <p>Configure os cards exibidos na seção de planos da landing page</p>
                </div>
                <div className="lp-toolbar-right">
                    <button className="lp-btn-preview" onClick={() => setPreviewOpen(v => !v)}>
                        {previewOpen ? <EyeOff size={15} /> : <Eye size={15} />}
                        {previewOpen ? 'Ocultar preview' : 'Ver preview'}
                    </button>
                    <button className="lp-btn-save" onClick={handleSave} disabled={saving}>
                        {saving
                            ? <><RefreshCw size={14} className="lp-spin" /> Salvando...</>
                            : <><Save size={14} /> Salvar Alterações</>
                        }
                    </button>
                </div>
            </div>

            {/* ══ WORKSPACE ══ */}
            <div className="lp-workspace">

                {/* ── EDITOR ── */}
                <div className="lp-editor">

                    <div className="lp-editor-header">
                        <h4>Editando card:</h4>
                        <div className="lp-slot-tabs">
                            <button
                                className={`lp-slot-tab lp-slot-main-tab ${activeSlot === 'main' ? 'active' : ''}`}
                                onClick={() => setActiveSlot('main')}
                            >
                                <Globe size={14} />
                                Card Principal
                                <span className="lp-slot-hint">destaque</span>
                            </button>
                            <button
                                className={`lp-slot-tab ${activeSlot === 'topRight' ? 'active' : ''}`}
                                onClick={() => setActiveSlot('topRight')}
                            >
                                <Globe size={14} />
                                Topo Direita
                                <span className="lp-slot-hint">menor</span>
                            </button>
                            <button
                                className={`lp-slot-tab ${activeSlot === 'bottomRight' ? 'active' : ''}`}
                                onClick={() => setActiveSlot('bottomRight')}
                            >
                                <Layers size={14} />
                                Base Direita
                                <span className="lp-slot-hint">combo</span>
                            </button>
                        </div>
                    </div>

                    {/* Grid de campos — 3 colunas */}
                    <div className="lp-editor-grid">

                        {/* Coluna 1: Textos da seção + Produto + Preço */}
                        <div className="lp-editor-panel">
                            <p className="lp-section-title">Textos da Seção</p>
                            <div className="lp-field-group">
                                <label>Título Principal</label>
                                <input
                                    type="text"
                                    value={config.sectionTitle}
                                    onChange={e => setConfig(prev => ({ ...prev, sectionTitle: e.target.value }))}
                                    className="lp-input"
                                />
                            </div>
                            <div className="lp-field-group">
                                <label>Subtítulo</label>
                                <input
                                    type="text"
                                    value={config.sectionSubtitle}
                                    onChange={e => setConfig(prev => ({ ...prev, sectionSubtitle: e.target.value }))}
                                    className="lp-input"
                                />
                            </div>

                            <p className="lp-section-title" style={{ marginTop: 8 }}>Produto — {slotLabel}</p>
                            <div className="lp-field-group">
                                <label>Produto (opcional)</label>
                                <select
                                    value={activeSlotData.productId}
                                    onChange={e => {
                                        updateSlot(activeSlotKey, 'productId', e.target.value);
                                        updateSlot(activeSlotKey, 'planIndex', 0);
                                    }}
                                    className="lp-select"
                                >
                                    <option value="">— Sem produto vinculado —</option>
                                    {products.map(p => (
                                        <option key={p._id} value={p._id}>{p.title} ({p.type})</option>
                                    ))}
                                </select>
                            </div>

                            {/* ✅ PREÇO LIVRE — duas linhas */}
                            <p className="lp-section-title" style={{ marginTop: 8 }}>Preço — {slotLabel}</p>
                            <div className="lp-field-group">
                                <label>Linha principal (destaque)</label>
                                <input
                                    type="text"
                                    value={activeSlotData.priceLine1}
                                    onChange={e => updateSlot(activeSlotKey, 'priceLine1', e.target.value)}
                                    placeholder='ex: R$ 197 à vista'
                                    className="lp-input"
                                />
                            </div>
                            <div className="lp-field-group">
                                <label>Linha secundária (menor)</label>
                                <input
                                    type="text"
                                    value={activeSlotData.priceLine2}
                                    onChange={e => updateSlot(activeSlotKey, 'priceLine2', e.target.value)}
                                    placeholder='ex: ou 4x de R$ 54,85'
                                    className="lp-input"
                                />
                            </div>
                        </div>

                        {/* Coluna 2: Textos do card + Badge */}
                        <div className="lp-editor-panel">
                            <p className="lp-section-title">Textos do Card — {slotLabel}</p>
                            <div className="lp-field-group">
                                <label>Título exibido</label>
                                <input
                                    type="text"
                                    value={activeSlotData.customTitle}
                                    onChange={e => updateSlot(activeSlotKey, 'customTitle', e.target.value)}
                                    placeholder="Deixe vazio para usar o título do produto"
                                    className="lp-input"
                                />
                            </div>
                            <div className="lp-field-group">
                                <label>Descrição curta</label>
                                <input
                                    type="text"
                                    value={activeSlotData.customDescription}
                                    onChange={e => updateSlot(activeSlotKey, 'customDescription', e.target.value)}
                                    placeholder="Descrição abaixo do título"
                                    className="lp-input"
                                />
                            </div>
                            <div className="lp-field-group">
                                <label>Texto do botão CTA</label>
                                <input
                                    type="text"
                                    value={activeSlotData.ctaText}
                                    onChange={e => updateSlot(activeSlotKey, 'ctaText', e.target.value)}
                                    className="lp-input"
                                />
                            </div>
                            <div className="lp-field-group">
                                <label>Nota de rodapé (opcional)</label>
                                <input
                                    type="text"
                                    value={activeSlotData.footerNote}
                                    onChange={e => updateSlot(activeSlotKey, 'footerNote', e.target.value)}
                                    placeholder="ex: Cancele quando quiser."
                                    className="lp-input"
                                />
                            </div>

                            {/* Badge — disponível para todos os slots */}
                            <div className="lp-field-group lp-field-inline">
                                <label>Mostrar badge</label>
                                <button
                                    className={`lp-toggle ${activeSlotData.showBadge ? 'active' : ''}`}
                                    onClick={() => updateSlot(activeSlotKey, 'showBadge', !activeSlotData.showBadge)}
                                >
                                    {activeSlotData.showBadge ? 'Sim' : 'Não'}
                                </button>
                            </div>
                            {activeSlotData.showBadge && (
                                <div className="lp-field-group">
                                    <label>Texto do badge</label>
                                    <input
                                        type="text"
                                        value={activeSlotData.badgeText}
                                        onChange={e => updateSlot(activeSlotKey, 'badgeText', e.target.value)}
                                        className="lp-input"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Coluna 3: Bullets — agora para TODOS os slots */}
                        <div className="lp-editor-panel">
                            <p className="lp-section-title">Funcionalidades incluídas — {slotLabel}</p>
                            <div className="lp-bullets-header">
                                <span style={{ fontSize: 12, color: '#9CA3AF' }}>
                                    {activeSlotData.customBullets.length} item(s)
                                </span>
                                <button className="lp-btn-add-bullet" onClick={() => addBullet(activeSlotKey)}>
                                    + Adicionar
                                </button>
                            </div>
                            <div className="lp-bullets-list">
                                {activeSlotData.customBullets.length === 0 ? (
                                    <p className="lp-bullets-empty">
                                        Nenhuma funcionalidade. Clique em "+ Adicionar" para incluir.
                                    </p>
                                ) : (
                                    activeSlotData.customBullets.map((bullet, i) => (
                                        <div key={i} className="lp-bullet-row">
                                            <input
                                                type="text"
                                                value={bullet}
                                                onChange={e => updateBullet(activeSlotKey, i, e.target.value)}
                                                className="lp-input"
                                            />
                                            <button
                                                className="lp-btn-remove-bullet"
                                                onClick={() => removeBullet(activeSlotKey, i)}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                    </div>
                </div>

                {/* ── PREVIEW ── */}
                {previewOpen && (
                    <div className="lp-preview-wrap">
                        <div className="lp-preview-label">
                            <Eye size={12} /> Preview — exatamente como aparece na landing page
                        </div>

                        <div className="lp-preview">
                            <div className="lp-prev-header">
                                <p className="lp-prev-label">Planos & Acesso</p>
                                <h2 className="lp-prev-headline">{config.sectionTitle}</h2>
                                <p className="lp-prev-sub">{config.sectionSubtitle}</p>
                            </div>

                            <div className="lp-prev-grid">

                                {/* ── CARD PRINCIPAL ── */}
                                <div className="lp-prev-main-card">
                                    {config.slotMain.showBadge && (
                                        <span className="lp-prev-badge">
                                            <Star size={9} fill="currentColor" />
                                            {config.slotMain.badgeText}
                                        </span>
                                    )}
                                    <p className="lp-prev-main-title">{getTitle(config.slotMain)}</p>
                                    <p className="lp-prev-main-desc">{getDescription(config.slotMain)}</p>

                                    {config.slotMain.customBullets.length > 0 && (
                                        <ul className="lp-prev-bullets">
                                            {config.slotMain.customBullets.map((item, i) => (
                                                <li key={i}><Check size={11} strokeWidth={2.5} />{item}</li>
                                            ))}
                                        </ul>
                                    )}

                                    <p className="lp-prev-anchor">Economicamente mais vantajoso do que adquirir módulos separadamente.</p>

                                    {/* ✅ PREÇO LIVRE */}
                                    <div className="lp-prev-price-block">
                                        {config.slotMain.priceLine1
                                            ? <span className="lp-prev-price-main">{config.slotMain.priceLine1}</span>
                                            : <span className="lp-prev-price-placeholder">Configure o preço →</span>
                                        }
                                        {config.slotMain.priceLine2 && (
                                            <span className="lp-prev-price-secondary">{config.slotMain.priceLine2}</span>
                                        )}
                                    </div>

                                    {config.slotMain.footerNote && (
                                        <p className="lp-prev-price-note">{config.slotMain.footerNote}</p>
                                    )}

                                    <button className="lp-prev-cta">
                                        {config.slotMain.ctaText}
                                        <ArrowRight size={13} strokeWidth={2} />
                                    </button>
                                </div>

                                {/* ── COLUNA DIREITA ── */}
                                <div className="lp-prev-right-col">

                                    {/* Card topo direita */}
                                    <div className="lp-prev-small-card">
                                        {config.slotTopRight.showBadge && (
                                            <span className="lp-prev-badge lp-prev-badge--light">
                                                <Star size={9} fill="currentColor" />
                                                {config.slotTopRight.badgeText}
                                            </span>
                                        )}
                                        <p className="lp-prev-small-title">{getTitle(config.slotTopRight)}</p>
                                        <p className="lp-prev-small-desc">{getDescription(config.slotTopRight)}</p>

                                        {/* ✅ Bullets do card direita topo */}
                                        {config.slotTopRight.customBullets.length > 0 && (
                                            <ul className="lp-prev-bullets lp-prev-bullets--small">
                                                {config.slotTopRight.customBullets.map((item, i) => (
                                                    <li key={i}><Check size={10} strokeWidth={2.5} />{item}</li>
                                                ))}
                                            </ul>
                                        )}

                                        {/* ✅ PREÇO LIVRE */}
                                        <div className="lp-prev-price-block lp-prev-price-block--small">
                                            {config.slotTopRight.priceLine1
                                                ? <span className="lp-prev-small-price-main">{config.slotTopRight.priceLine1}</span>
                                                : <span className="lp-prev-price-placeholder">Configure o preço →</span>
                                            }
                                            {config.slotTopRight.priceLine2 && (
                                                <span className="lp-prev-price-secondary lp-prev-price-secondary--dark">{config.slotTopRight.priceLine2}</span>
                                            )}
                                        </div>

                                        {config.slotTopRight.footerNote && (
                                            <p className="lp-prev-small-note">{config.slotTopRight.footerNote}</p>
                                        )}

                                        <button className="lp-prev-small-cta">
                                            {config.slotTopRight.ctaText}
                                            <ArrowRight size={11} strokeWidth={2} />
                                        </button>
                                    </div>

                                    {/* Card base direita */}
                                    <div className="lp-prev-small-card lp-prev-combo-card">
                                        <div className="lp-prev-combo-header">
                                            <ChevronDown size={12} />
                                            <div style={{ flex: 1 }}>
                                                {config.slotBottomRight.showBadge && (
                                                    <span className="lp-prev-badge lp-prev-badge--light" style={{ marginBottom: 4 }}>
                                                        <Star size={9} fill="currentColor" />
                                                        {config.slotBottomRight.badgeText}
                                                    </span>
                                                )}
                                                <p className="lp-prev-small-title">{getTitle(config.slotBottomRight)}</p>
                                                <p className="lp-prev-small-desc">{getDescription(config.slotBottomRight)}</p>
                                            </div>
                                            {/* ✅ Preço compacto no header do combo */}
                                            {config.slotBottomRight.priceLine1 && (
                                                <div className="lp-prev-combo-price-block">
                                                    <span className="lp-prev-combo-price">{config.slotBottomRight.priceLine1}</span>
                                                    {config.slotBottomRight.priceLine2 && (
                                                        <span className="lp-prev-combo-price-secondary">{config.slotBottomRight.priceLine2}</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* ✅ Bullets do combo */}
                                        {config.slotBottomRight.customBullets.length > 0 && (
                                            <ul className="lp-prev-bullets lp-prev-bullets--small" style={{ marginTop: 8 }}>
                                                {config.slotBottomRight.customBullets.map((item, i) => (
                                                    <li key={i}><Check size={10} strokeWidth={2.5} />{item}</li>
                                                ))}
                                            </ul>
                                        )}

                                        {config.slotBottomRight.footerNote && (
                                            <p className="lp-prev-small-note" style={{ marginTop: 8 }}>{config.slotBottomRight.footerNote}</p>
                                        )}

                                        <button className="lp-prev-small-cta">
                                            {config.slotBottomRight.ctaText}
                                            <ArrowRight size={11} strokeWidth={2} />
                                        </button>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default LandingProducts;